import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { JobService } from 'src/@core/services/job/job.service';
import { NotificationService } from 'src/@core/services/notification.service';
import { EnterpriseAdminService } from 'src/app/modules/enterprise-admin/enterprise-admin.service';
import { CallHistoryQuery, CallHistoryResult } from '../models/entity-leads.types';
import { enrichCallRows, normalizePagination, parseOutcomeNamesFromResponse } from '../utils/entity-leads.utils';

const EMPTY_CALL_HISTORY: CallHistoryResult = { rows: [], pagination: null };

@Injectable({ providedIn: 'root' })
export class EntityLeadsFacadeService {
  private cachedEntityId: string | null = null;
  private projectsCache$: Observable<any[]> | null = null;
  private projectDetailsCache = new Map<string, Observable<any>>();
  private allProjectsOutcomesCache = new Map<string, Observable<string[]>>();

  private readonly tableQuery$ = new Subject<CallHistoryQuery>();
  private readonly outcomesQuery$ = new Subject<{ entityId: string; projectId?: string }>();

  readonly loadingProjects$ = new BehaviorSubject<boolean>(false);
  readonly loadingOutcomes$ = new BehaviorSubject<boolean>(false);
  readonly loadingTable$ = new BehaviorSubject<boolean>(false);

  readonly projects$ = new BehaviorSubject<any[]>([]);
  readonly outcomeOptions$ = new BehaviorSubject<string[]>([]);

  readonly tableResult$ = this.tableQuery$.pipe(
    tap(() => this.loadingTable$.next(true)),
    switchMap((query) => this.fetchCallHistoryOnce(query).pipe(
      finalize(() => this.loadingTable$.next(false))
    ))
  );

  constructor(
    private jobService: JobService,
    private enterpriseAdminService: EnterpriseAdminService,
    private notify: NotificationService,
  ) {
    this.outcomesQuery$.pipe(
      switchMap(({ entityId, projectId }) => this.fetchOutcomeOptionsOnce(entityId, projectId))
    ).subscribe((options) => this.outcomeOptions$.next(options));
  }

  loadProjects(entityId: string): void {
    if (this.cachedEntityId === entityId && this.projectsCache$) {
      this.projectsCache$.subscribe((items) => this.projects$.next(items));
      return;
    }

    this.cachedEntityId = entityId;
    this.loadingProjects$.next(true);
    this.projectsCache$ = this.jobService.getAllProjects(entityId, 1, 100, 'created_at', 'desc', []).pipe(
      map((res: any) => (res && res.items) ? res.items : []),
      tap((items) => this.projects$.next(items)),
      catchError(() => {
        this.projects$.next([]);
        return of([]);
      }),
      finalize(() => this.loadingProjects$.next(false)),
      shareReplay(1)
    );
    this.projectsCache$.subscribe();
  }

  loadOutcomeOptions(entityId: string, projectId?: string): void {
    this.loadingOutcomes$.next(true);
    this.outcomesQuery$.next({ entityId, projectId });
  }

  loadTable(query: CallHistoryQuery): void {
    this.tableQuery$.next(query);
  }

  getProjectDetails(entityId: string, projectId: string): Observable<any> {
    return this.getHoaProjectCached(entityId, projectId);
  }

  clearProjectDetailsCache(): void {
    this.projectDetailsCache.clear();
    this.allProjectsOutcomesCache.clear();
  }

  private getHoaProjectCached(entityId: string, projectId?: string): Observable<any> {
    const cacheKey = `${entityId}:${projectId}`;
    if (!this.projectDetailsCache.has(cacheKey)) {
      const request$ = this.jobService.getHoaProject(entityId, projectId).pipe(
        catchError(() => of(null)),
        shareReplay(1)
      );
      this.projectDetailsCache.set(cacheKey, request$);
    }
    return this.projectDetailsCache.get(cacheKey)!;
  }

  // Outcome pills: a single project shows its own expected_outcomes (getHoaProject),
  // while the "all projects" view uses the entity-wide outcomes filter, which returns
  // a flat, deduped, ordered list (base outcomes first, then project-specific ones).
  private fetchOutcomeOptionsOnce(entityId: string, projectId?: string): Observable<string[]> {
    const isSingleProject = projectId != null && String(projectId).trim() !== '';
    const outcomes$ = isSingleProject
      ? this.getHoaProjectCached(entityId, projectId).pipe(
          map((res) => parseOutcomeNamesFromResponse(res)),
          catchError(() => of([] as string[]))
        )
      : this.fetchEntityOutcomes(entityId);

    return outcomes$.pipe(finalize(() => this.loadingOutcomes$.next(false)));
  }

  private fetchEntityOutcomes(entityId: string): Observable<string[]> {
    if (!entityId) {
      return of([] as string[]);
    }
    if (!this.allProjectsOutcomesCache.has(entityId)) {
      const request$ = this.jobService.getHoaEntityOutcomesFilter(entityId).pipe(
        map((res) => this.extractEntityOutcomeNames(res)),
        catchError(() => {
          this.notify.showMessage('Unable to load outcomes. Please try again.', 5000);
          return of([] as string[]);
        }),
        shareReplay(1)
      );
      this.allProjectsOutcomesCache.set(entityId, request$);
    }
    return this.allProjectsOutcomesCache.get(entityId)!;
  }

  // Preserve the order returned by the API (base outcomes first, then project-specific
  // ones); only drop blanks and de-duplicate.
  private extractEntityOutcomeNames(res: any): string[] {
    const list = res && Array.isArray(res.outcomes) ? res.outcomes : [];
    const seen = new Set<string>();
    const names: string[] = [];
    list.forEach((value: any) => {
      const name = typeof value === 'string' ? value.trim() : '';
      if (name && !seen.has(name)) {
        seen.add(name);
        names.push(name);
      }
    });
    return names;
  }

  private fetchCallHistoryOnce(query: CallHistoryQuery): Observable<CallHistoryResult> {
    const projectId = query.projectId && String(query.projectId).trim() !== ''
      ? query.projectId
      : undefined;

    return this.enterpriseAdminService.getCallHistoryProject(
      query.entityId,
      projectId,
      query.pageSize,
      query.pageIndex,
      undefined,
      query.filters
    ).pipe(
      map((res: any) => {
        const calls = (res && res.data && Array.isArray(res.data)) ? res.data : [];
        return {
          rows: enrichCallRows(calls, !!query.formatStartTime),
          pagination: normalizePagination(res && res.pagination),
        };
      }),
      catchError(() => of(EMPTY_CALL_HISTORY))
    );
  }
}
